import { Client, Databases, Query } from "appwrite";

const client = new Client()
    .setEndpoint(import.meta.env.VITE_APPWRITE_ENDPOINT)
    .setProject(import.meta.env.VITE_APPWRITE_PROJECT_ID);

const databases = new Databases(client);

// 🔹 IDs de base y colección donde guardas las suscripciones
const DATABASE_ID = import.meta.env.VITE_APPWRITE_DATABASE_ID;
const COLLECTION_ID = import.meta.env.VITE_WEB_PUSH;

// 🔹 Clave pública de VAPID
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY;

// 🔹 Detectar tipo de dispositivo (CLAVE para notificaciones móviles)
function getDeviceInfo() {
    const userAgent = navigator.userAgent.toLowerCase();
    
    let deviceType = 'desktop';
    let platform = 'unknown';
    
    // Detectar móviles
    if (/android/i.test(userAgent)) {
        deviceType = 'mobile';
        platform = 'android';
    } else if (/iphone|ipad|ipod/i.test(userAgent)) {
        deviceType = 'mobile';
        platform = 'ios';
    } else if (/windows/i.test(userAgent)) {
        platform = 'windows';
    } else if (/mac/i.test(userAgent)) {
        platform = 'macos';
    } else if (/linux/i.test(userAgent)) {
        platform = 'linux';
    }
    
    return { deviceType, platform };
}

// 🔹 Función MEJORADA para verificar duplicados por múltiples criterios
async function checkExistingSubscription(userId: string, deviceFingerprint: string, endpoint?: string) {
    try {
        // Buscar por device fingerprint (método original)
        const deviceSearch = await databases.listDocuments(DATABASE_ID, COLLECTION_ID, [
            Query.equal("userId", userId),
            Query.equal("device", deviceFingerprint)
        ]);

        if (deviceSearch.documents.length > 0) {
            console.log('Encontrada suscripción por device fingerprint');
            return deviceSearch.documents[0];
        }

        // Si tenemos endpoint, buscar también por endpoint para detectar duplicados
        if (endpoint) {
            const endpointSearch = await databases.listDocuments(DATABASE_ID, COLLECTION_ID, [
                Query.equal("userId", userId),
                Query.equal("endpoint", endpoint)
            ]);

            if (endpointSearch.documents.length > 0) {
                console.log('Encontrada suscripción duplicada por endpoint');
                return endpointSearch.documents[0];
            }
        }

        // Buscar suscripciones similares del usuario para limpiar duplicados
        const userSubscriptions = await databases.listDocuments(DATABASE_ID, COLLECTION_ID, [
            Query.equal("userId", userId)
        ]);

        if (userSubscriptions.documents.length > 1) {
            console.log(`🧹 Detectadas ${userSubscriptions.documents.length} suscripciones, limpiando duplicados...`);
            await cleanupDuplicateSubscriptions(userSubscriptions.documents, deviceFingerprint);
        }

        return null;
    } catch (error) {
        console.error("Error verificando suscripción existente:", error);
        return null;
    }
}

// 🔹 Función para limpiar suscripciones duplicadas
async function cleanupDuplicateSubscriptions(documents: any[], currentDeviceFingerprint: string) {
    try {
        if (documents.length <= 1) return;

        console.log(` Limpiando ${documents.length} suscripciones duplicadas...`);
        
        // Mantener solo la más reciente o la que coincida con el device actual
        let keepDocument = documents[0];
        
        // Priorizar documento que coincida con device fingerprint actual
        const exactMatch = documents.find(doc => doc.device === currentDeviceFingerprint);
        if (exactMatch) {
            keepDocument = exactMatch;
        } else {
            // Mantener el más reciente
            keepDocument = documents.reduce((latest, current) => 
                new Date(current.$createdAt) > new Date(latest.$createdAt) ? current : latest
            );
        }
        
        // Eliminar duplicados
        const toDelete = documents.filter(doc => doc.$id !== keepDocument.$id);
        
        for (const doc of toDelete) {
            try {
                await databases.deleteDocument(DATABASE_ID, COLLECTION_ID, doc.$id);
                console.log(` Eliminada suscripción duplicada: ${doc.$id}`);
            } catch (deleteError) {
                console.warn(` Error eliminando duplicado ${doc.$id}:`, deleteError);
            }
        }
        
        console.log(` Limpieza completada. Mantenida: ${keepDocument.$id}`);
        
    } catch (error) {
        console.error("Error en limpieza de duplicados:", error);
    }
}

// 🔹 Tu función original de fingerprint (MANTENIDA para compatibilidad)
function getDeviceFingerprint(): string {
    try {
        const deviceInfo = getDeviceInfo();
        
        // Información básica que funciona en todos los dispositivos
        const screenInfo = `${screen.width}x${screen.height}x${screen.colorDepth || 24}`;
        const timezoneOffset = new Date().getTimezoneOffset().toString();
        const platformInfo = navigator.platform || 'unknown-platform';
        const language = navigator.language || (navigator as any).languages?.[0] || 'unknown-lang';
        
        // Información adicional para móviles
        const deviceMemory = (navigator as any).deviceMemory || 'unknown';
        const hardwareConcurrency = navigator.hardwareConcurrency || 'unknown';
        const maxTouchPoints = navigator.maxTouchPoints || 0;

        // Canvas fingerprint con fallback
        let canvasFingerprint = 'canvas-unavailable';
        try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.textBaseline = 'top';
                ctx.font = '14px Arial';
                ctx.fillText(`${deviceInfo.platform} fingerprint`, 2, 2);
                canvasFingerprint = canvas.toDataURL().slice(-30); // Solo los últimos 30 chars
            }
        } catch (canvasError) {
            // Silenciar errores de canvas
        }

        const fingerprint = [
            deviceInfo.platform, // Incluir plataforma al inicio
            navigator.userAgent.substring(0, 100), // Limitar userAgent
            language,
            screenInfo,
            timezoneOffset,
            platformInfo,
            deviceMemory,
            hardwareConcurrency,
            maxTouchPoints,
            canvasFingerprint,
            `${window.innerWidth}x${window.innerHeight}`, // Viewport
        ].join('|');
        
        // Crear hash
        let hash = 0;
        for (let i = 0; i < fingerprint.length; i++) {
            const char = fingerprint.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash;
        }
        
        // Retornar con prefijo de plataforma para mejor identificación
        return `${deviceInfo.platform}-${Math.abs(hash).toString(36)}`;
    } catch (error) {
        const deviceInfo = getDeviceInfo();
        // Fallback para casos extremos
        return `${deviceInfo.platform}-fallback-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }
}

// 🔹 Función específica para manejar suscripciones móviles (SIN CAMBIOS)
async function createMobilePushSubscription(swReg: ServiceWorkerRegistration): Promise<PushSubscription | null> {
    try {
        const deviceInfo = getDeviceInfo();
        
        // Para móviles, primero verificar si hay una suscripción existente
        let existingSubscription = await swReg.pushManager.getSubscription();
        
        if (existingSubscription) {
            console.log(`📱 [${deviceInfo.platform}] Suscripción existente encontrada`);
            
            // Verificar que el endpoint sea válido
            if (existingSubscription.endpoint && existingSubscription.endpoint.length > 10) {
                // Para Android, verificar que sea FCM
                if (deviceInfo.platform === 'android' && existingSubscription.endpoint.includes('fcm.googleapis.com')) {
                    return existingSubscription;
                }
                // Para iOS y otros, aceptar el endpoint existente
                if (deviceInfo.platform !== 'android') {
                    return existingSubscription;
                }
            }
            
            // Si el endpoint no es válido, eliminar la suscripción
            console.log(` [${deviceInfo.platform}] Endpoint inválido, eliminando suscripción...`);
            await existingSubscription.unsubscribe();
        }

        // Crear nueva suscripción con configuración optimizada para móviles
        console.log(` [${deviceInfo.platform}] Creando nueva suscripción...`);
        
        const subscription = await swReg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        });

        // Verificar que el endpoint sea correcto
        if (!subscription.endpoint || subscription.endpoint.length < 10) {
            throw new Error('Endpoint inválido generado');
        }

        // Para Android, verificar que sea FCM
        if (deviceInfo.platform === 'android' && !subscription.endpoint.includes('fcm.googleapis.com')) {
            console.warn(' Endpoint de Android no es FCM:', subscription.endpoint.substring(0, 50));
        }

        console.log(`✅ [${deviceInfo.platform}] Suscripción creada con endpoint:`, 
                   subscription.endpoint.substring(0, 50) + '...');

        return subscription;
    } catch (error) {
        console.error(' Error creando suscripción móvil:', error);
        return null;
    }
}

// 🔹 Función principal HÍBRIDA (tu lógica + anti-duplicados)
export async function registerPushSubscription(userId: string) {
    try {
        const deviceInfo = getDeviceInfo();
        console.log(' [registerPush] Iniciando registro para:', deviceInfo);
        
        // 1. Verificar soporte básico
        if (!("serviceWorker" in navigator)) {
            console.warn("[registerPush] Service Worker no soportado");
            return false;
        }
        if (!("PushManager" in window)) {
            console.warn("[registerPush] Push API no soportada");
            return false;
        }
        if (!("Notification" in window)) {
            console.warn("[registerPush] Notifications API no soportada");
            return false;
        }

        // 2. Verificar HTTPS (crítico para móviles)
        if (location.protocol !== 'https:' && location.hostname !== 'localhost') {
            console.warn("[registerPush] HTTPS requerido para notificaciones");
            return false;
        }

        // 3. Generar fingerprint del dispositivo
        const deviceFingerprint = getDeviceFingerprint();
        console.log(' [registerPush] Device fingerprint:', deviceFingerprint);

        // 4. 🚀 VERIFICACIÓN MEJORADA: Buscar suscripciones existentes
        const existingSubscription = await checkExistingSubscription(userId, deviceFingerprint);
        if (existingSubscription) {
            console.log(" Ya existe suscripción para este dispositivo en BD");
            return true;
        }

        // 5. ⭐ SOLICITAR PERMISOS CORRECTAMENTE (esto estaba fallando)
        let permission = Notification.permission;
        console.log('[registerPush] Permission inicial:', permission);
        
        // ⭐ FORZAR SOLICITUD DE PERMISOS si no están concedidos
        if (permission !== "granted") {
            console.log(` [registerPush] Solicitando permisos para ${deviceInfo.platform}...`);
            try {
                // Para móviles, agregar un pequeño delay
                if (deviceInfo.deviceType === 'mobile') {
                    await new Promise(resolve => setTimeout(resolve, 300));
                }
                
                // ⭐ AQUÍ ESTABA EL PROBLEMA - siempre solicitar permisos si no están granted
                permission = await Notification.requestPermission();
                console.log(' [registerPush] Permission después de solicitar:', permission);
            } catch (permError) {
                console.warn('[registerPush] Error solicitando permisos:', permError);
                return false;
            }
        }

        // Verificar que se concedieron los permisos
        if (permission === "denied") {
            console.warn("[registerPush] Permisos denegados por el usuario");
            return false;
        }

        if (permission !== "granted") {
            console.warn("[registerPush] Permisos no concedidos:", permission);
            return false;
        }

        // 6. Obtener Service Worker (compatible con VitePWA)
        let swReg;
        try {
            console.log(' [registerPush] Esperando Service Worker...');
            
            // Para móviles, aumentar el timeout
            const timeout = deviceInfo.deviceType === 'mobile' ? 15000 : 8000;
            
            swReg = await Promise.race([
                navigator.serviceWorker.ready,
                new Promise((_, reject) => 
                    setTimeout(() => reject(new Error('Service Worker timeout')), timeout)
                )
            ]) as ServiceWorkerRegistration;
            
            console.log(' [registerPush] Service Worker obtenido');
            
            // Verificar que esté activo
            if (!swReg.active) {
                console.warn('[registerPush] Service Worker no está activo, esperando...');
                await new Promise(resolve => setTimeout(resolve, 2000));
                
                if (!swReg.active) {
                    console.error('[registerPush] Service Worker sigue inactivo');
                    return false;
                }
            }
        } catch (swError) {
            console.error("[registerPush] Error obteniendo service worker:", swError);
            return false;
        }

        // 7. Verificar PushManager
        if (!swReg.pushManager) {
            console.error("[registerPush] PushManager no disponible en este navegador");
            return false;
        }

        // 8. Crear suscripción usando función especializada
        let subscription;
        
        if (deviceInfo.deviceType === 'mobile') {
            subscription = await createMobilePushSubscription(swReg);
        } else {
            // Para desktop, usar método tradicional
            try {
                console.log('💻 [registerPush] Creando suscripción desktop...');
                subscription = await swReg.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
                });
                console.log(' [registerPush] Suscripción desktop creada');
            } catch (subError) {
                console.error('[registerPush] Error creando suscripción desktop:', subError);
                return false;
            }
        }

        if (!subscription) {
            console.error("[registerPush] No se pudo crear la suscripción");
            return false;
        }

        // 9. ⭐ SEGUNDA VERIFICACIÓN antes de guardar (prevenir race conditions)
        const duplicateCheck = await checkExistingSubscription(userId, deviceFingerprint, subscription.endpoint);
        if (duplicateCheck) {
            console.log(" Suscripción ya existe (verificación final), no se duplicará");
            return true;
        }

        // 10. Extraer claves con validación
        const p256dhKey = subscription.getKey("p256dh");
        const authKeyBuffer = subscription.getKey("auth");
        
        if (!p256dhKey || !authKeyBuffer) {
            console.error("[registerPush] No se pudieron obtener las claves de encriptación");
            return false;
        }
        
        const p256dh = arrayBufferToBase64(p256dhKey);
        const authKey = arrayBufferToBase64(authKeyBuffer);

        // 11. Validar datos antes de guardar
        if (!subscription.endpoint || !p256dh || !authKey) {
            console.error("[registerPush] Datos de suscripción incompletos");
            return false;
        }

        // 12. ⭐ GUARDAR CON PROTECCIÓN CONTRA DUPLICADOS
        try {
            const subscriptionData = {
                endpoint: subscription.endpoint,
                expirationTime: "", // Siempre vacío como solicitaste
                userId: userId,
                device: deviceFingerprint,
                auth: authKey,
                p256dh: p256dh,
            };

            console.log('[registerPush] Guardando en Appwrite:', {
                endpoint: subscription.endpoint.substring(0, 50) + '...',
                userId,
                device: deviceFingerprint,
                platform: deviceInfo.platform
            });

            // ⭐ Usar ID único para prevenir duplicados
            const uniqueId = `${userId.replace(/[^a-zA-Z0-9]/g, '')}_${deviceFingerprint}`;
            
            const result = await databases.createDocument(DATABASE_ID, COLLECTION_ID, uniqueId, subscriptionData);
            console.log(" [registerPush] Suscripción guardada en Appwrite:", result);
            
            return true;
        } catch (dbError: any) {
            console.error("[registerPush] Error guardando en BD:", dbError);
            
            // Si es documento duplicado, considerarlo éxito
            if (dbError.message?.includes('Document with the requested ID already exists') ||
                dbError.message?.includes('already exists') ||
                dbError.code === 409) {
                console.log(" [registerPush] Documento ya existe (evitado duplicado)");
                return true;
            }
            
            // ⭐ Fallback: Intentar con ID único() si falla el ID personalizado
            try {
                console.log(" [registerPush] Reintentando con ID aleatorio...");
                const fallbackResult = await databases.createDocument(DATABASE_ID, COLLECTION_ID, "unique()", subscription);
                console.log("[registerPush] Guardado con ID aleatorio:", fallbackResult.$id);
                return true;
            } catch (fallbackError) {
                console.error("[registerPush] Error en fallback:", fallbackError);
                return false;
            }
        }

    } catch (err) {
        console.error(" [registerPush] Error general:", err);
        return false;
    }
}

// 🔹 Función para solicitar permisos manualmente (MEJORADA)
export async function requestNotificationPermission(): Promise<boolean> {
    try {
        const deviceInfo = getDeviceInfo();
        
        if (!("Notification" in window)) {
            console.warn("Notificaciones no soportadas en este dispositivo");
            return false;
        }

        if (location.protocol !== 'https:' && location.hostname !== 'localhost') {
            console.warn("HTTPS requerido para notificaciones");
            return false;
        }

        console.log(` Solicitando permisos explícitamente para ${deviceInfo.platform}...`);
        
        // Para móviles, agregar delay antes de solicitar
        if (deviceInfo.deviceType === 'mobile') {
            await new Promise(resolve => setTimeout(resolve, 500));
        }
        
        const permission = await Notification.requestPermission();
        console.log(' Permiso resultado:', permission);
        
        return permission === "granted";
    } catch (error) {
        console.error("Error solicitando permisos:", error);
        return false;
    }
}

// 🔹 Función para verificar el estado actual de la suscripción (MEJORADA)
export async function checkPushSubscriptionStatus(userId: string): Promise<{
    hasPermission: boolean;
    hasSubscription: boolean;
    deviceInfo: any;
    endpoint?: string;
    subscriptionCount?: number;
}> {
    try {
        const deviceInfo = getDeviceInfo();
        const deviceFingerprint = getDeviceFingerprint();
        
        const hasPermission = Notification.permission === 'granted';
        const existingSubscription = await checkExistingSubscription(userId, deviceFingerprint);
        
        // Contar total de suscripciones del usuario
        const allUserSubscriptions = await databases.listDocuments(DATABASE_ID, COLLECTION_ID, [
            Query.equal("userId", userId)
        ]);
        
        return {
            hasPermission,
            hasSubscription: !!existingSubscription,
            deviceInfo,
            endpoint: existingSubscription?.endpoint?.substring(0, 50) + '...',
            subscriptionCount: allUserSubscriptions.total
        };
    } catch (error) {
        console.error('Error checking subscription status:', error);
        return {
            hasPermission: false,
            hasSubscription: false,
            deviceInfo: getDeviceInfo(),
            subscriptionCount: 0
        };
    }
}

// 🔹 Nueva función para limpiar suscripciones duplicadas manualmente
export async function cleanupUserSubscriptions(userId: string): Promise<{
    success: boolean;
    removed: number;
    remaining: number;
}> {
    try {
        console.log('🧹 Iniciando limpieza de suscripciones para usuario:', userId);
        
        const allUserSubscriptions = await databases.listDocuments(DATABASE_ID, COLLECTION_ID, [
            Query.equal("userId", userId)
        ]);
        
        if (allUserSubscriptions.total <= 1) {
            return {
                success: true,
                removed: 0,
                remaining: allUserSubscriptions.total
            };
        }
        
        const deviceFingerprint = getDeviceFingerprint();
        await cleanupDuplicateSubscriptions(allUserSubscriptions.documents, deviceFingerprint);
        
        // Verificar cuántas quedan
        const remainingSubscriptions = await databases.listDocuments(DATABASE_ID, COLLECTION_ID, [
            Query.equal("userId", userId)
        ]);
        
        const removed = allUserSubscriptions.total - remainingSubscriptions.total;
        
        return {
            success: true,
            removed,
            remaining: remainingSubscriptions.total
        };
        
    } catch (error) {
        console.error('Error limpiando suscripciones:', error);
        return {
            success: false,
            removed: 0,
            remaining: 0
        };
    }
}

// Funciones auxiliares
function urlBase64ToUint8Array(base64String: string | any[]) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
        .replace(/-/g, "+")
        .replace(/_/g, "/");

    const rawData = window.atob(base64);
    return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

function arrayBufferToBase64(buffer: ArrayBuffer | null) {
    if (!buffer) return "";
    const uint8Array = new Uint8Array(buffer);
    const binaryString = String.fromCharCode(...Array.from(uint8Array));
    return btoa(binaryString);
}